import React, { useRef, useEffect, useState } from "react";
import { motion, useScroll, useTransform, useInView, useReducedMotion } from "framer-motion";
import { ArrowRight, Play } from "lucide-react";
import { Button } from "../ui/button";
import MagneticButton from "../ui/MagneticButton";
import HeroHUD from "./HeroHUD";
import { useIsMobile } from "../../hooks/useIsMobile";
import { api, BASE } from "../../services/api";

function resolveImg(url) {
    if (!url) return null;
    return url.startsWith("http") ? url : `${BASE}${url}`;
}

function HeroBrands() {
    const [brands, setBrands] = useState([]);
    useEffect(() => { api.getBrands().then(setBrands).catch(() => {}); }, []);
    if (!brands.length) return null;

    return (
        <div className="absolute bottom-0 left-0 right-0 z-20 border-t border-white/6 bg-nova-black/40 backdrop-blur-sm">
            <div className="max-w-7xl mx-auto px-6 sm:px-12 py-4 flex items-center gap-6 sm:gap-10 overflow-x-auto no-scrollbar">
                <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-600 shrink-0 hidden sm:block">
                    Technika
                </span>
                <div className="w-px h-5 bg-white/8 shrink-0 hidden sm:block" />
                <div className="flex items-center gap-6 sm:gap-8">
                    {brands.map((b) => (
                        <div key={b.id} className="shrink-0 group cursor-default">
                            {b.logo_url ? (
                                <img
                                    src={resolveImg(b.logo_url)}
                                    alt={b.name}
                                    className="h-6 sm:h-7 w-auto object-contain select-none transition-opacity duration-300 opacity-40 group-hover:opacity-85"
                                    draggable={false}
                                />
                            ) : (
                                <span className="font-heading font-black text-xs tracking-tight text-white/25 group-hover:text-white/70 transition-colors duration-300">
                                    {b.name}
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

const VIDEO_SRC = "/draftly-video.mp4";
const CROSSFADE  = 1.5;

// ── Dual-video seamless loop ──────────────────────────────────────────────────
function VideoLoop({ parallaxStyle, isMobile }) {
    const v0 = useRef(null);
    const v1 = useRef(null);
    const front    = useRef(0);
    const swapping = useRef(false);
    const [frontState, setFrontState] = useState(0);

    useEffect(() => {
        const els = [v0.current, v1.current];
        if (!els[0] || !els[1]) return;
        els[0].play().catch(() => {});

        // On mobile use a single-video loop — no crossfade needed (saves CPU)
        if (isMobile) return;

        const id = setInterval(() => {
            const i   = front.current;
            const cur = els[i];
            if (!cur || !cur.duration || isNaN(cur.duration) || swapping.current) return;
            const fadeAt    = Math.min(CROSSFADE, cur.duration * 0.2);
            const remaining = cur.duration - cur.currentTime;
            if (remaining > 0 && remaining <= fadeAt) {
                swapping.current = true;
                const nxt = els[1 - i];
                nxt.currentTime = 0;
                nxt.play().catch(() => {});
                front.current = 1 - i;
                setFrontState(1 - i);
                setTimeout(() => {
                    cur.pause();
                    cur.currentTime = 0;
                    swapping.current = false;
                }, (CROSSFADE + 0.3) * 1000);
            }
        }, 200);
        return () => clearInterval(id);
    }, [isMobile]);

    const base = "absolute inset-0 w-full h-full object-cover";
    const td   = `${Math.round(CROSSFADE * 1000)}ms`;

    // On mobile: no parallax wrapper, single video with native loop
    if (isMobile) {
        return (
            <div className="absolute inset-0 w-full h-full">
                <video
                    ref={v0}
                    muted
                    loop
                    playsInline
                    autoPlay
                    preload="auto"
                    className={`${base}`}
                >
                    <source src={VIDEO_SRC} type="video/mp4" />
                </video>
            </div>
        );
    }

    return (
        <motion.div style={parallaxStyle} className="absolute inset-0 w-full h-[120%] -top-[10%]">
            {[v0, v1].map((ref, i) => (
                <video
                    key={i}
                    ref={ref}
                    muted
                    playsInline
                    preload="auto"
                    className={`${base} ease-in-out`}
                    style={{ opacity: frontState === i ? 1 : 0, transitionProperty: "opacity", transitionDuration: td }}
                >
                    <source src={VIDEO_SRC} type="video/mp4" />
                </video>
            ))}
        </motion.div>
    );
}

// ── Animated counter ──────────────────────────────────────────────────────────
function AnimatedCounter({ target, suffix = "", duration = 2 }) {
    const [n, setN]  = useState(0);
    const ref        = useRef(null);
    const inView     = useInView(ref, { once: true, margin: "-50px" });
    const noMotion   = useReducedMotion();

    useEffect(() => {
        if (!inView) return;
        const end = parseInt(target);
        if (noMotion) { setN(end); return; }
        const startTime = performance.now();
        const ms  = duration * 1000;
        const tick = (now) => {
            const p = Math.min((now - startTime) / ms, 1);
            setN(Math.floor(end * (1 - Math.pow(1 - p, 3))));
            if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
    }, [inView, target, duration, noMotion]);

    return <span ref={ref}>{n}{suffix}</span>;
}

// ── Hero ──────────────────────────────────────────────────────────────────────
export default function Hero() {
    const sectionRef  = useRef(null);
    const isMobile    = useIsMobile();
    const prefersNoMotion = useReducedMotion();
    const noMotion    = isMobile || prefersNoMotion;

    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ["start start", "end start"],
    });

    // These transforms are only used on desktop
    const bgY           = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
    const contentY      = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);
    const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

    const scrollTo = (id) => {
        const el = document.querySelector(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <section
            ref={sectionRef}
            id="hero"
            className="relative min-h-screen w-full flex items-center overflow-hidden noise"
            data-testid="hero-section"
        >
            {/* Background */}
            <div className="absolute inset-0 z-0 overflow-hidden">
                {prefersNoMotion ? (
                    // Reduced motion: static dark gradient, no video
                    <div className="absolute inset-0 bg-gradient-to-br from-nova-black via-nova-surface/40 to-nova-black" />
                ) : (
                    <VideoLoop parallaxStyle={{ y: bgY }} isMobile={isMobile} />
                )}
                <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-nova-black sm:from-nova-black/55 sm:via-nova-black/45 sm:to-nova-black" />
                <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-black/20 to-transparent sm:from-nova-black sm:via-nova-black/60 sm:to-transparent" />
            </div>

            {/* HUD corners — lightweight, keep on all devices */}
            <HeroHUD />

            {/* Glow blobs — hide on mobile to save paint */}
            {!isMobile && (
                <>
                    <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-nova-purple/15 rounded-full blur-[160px] z-10 pointer-events-none" />
                    <div className="absolute bottom-10 left-10 w-[400px] h-[400px] bg-nova-cyan/10 rounded-full blur-[140px] z-10 pointer-events-none" />
                </>
            )}

            {/* Content — parallax only on desktop */}
            <motion.div
                style={noMotion ? {} : { y: contentY, opacity: contentOpacity }}
                className="relative z-20 max-w-7xl mx-auto px-6 sm:px-12 w-full py-32"
            >
                <motion.div
                    initial={{ opacity: 0, y: noMotion ? 0 : 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: noMotion ? 0 : 0.8, delay: noMotion ? 0 : 0.1 }}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-sm border border-nova-cyan/25 bg-nova-cyan/5 mb-8"
                    data-testid="hero-badge"
                >
                    <span className="w-2 h-2 rounded-full bg-nova-cyan animate-pulse" />
                    <span className="font-mono text-xs uppercase tracking-[0.2em] text-nova-cyan">
                        Moving heads · Line array · LED
                    </span>
                </motion.div>

                <motion.h1
                    initial={{ opacity: 0, y: noMotion ? 0 : 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: noMotion ? 0 : 0.9, delay: noMotion ? 0 : 0.2 }}
                    className="font-heading text-4xl sm:text-5xl lg:text-7xl font-black tracking-tighter leading-[1.02] max-w-5xl"
                    data-testid="hero-title"
                >
                    Profesionálne{" "}
                    <span className="text-gradient">ozvučenie</span>{" "}
                    a{" "}
                    <span className="text-gradient">osvetlenie</span>{" "}
                    pre vaše podujatie
                </motion.h1>

                <motion.p
                    initial={{ opacity: 0, y: noMotion ? 0 : 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: noMotion ? 0 : 0.8, delay: noMotion ? 0 : 0.4 }}
                    className="mt-8 max-w-2xl text-lg sm:text-xl text-zinc-400 leading-relaxed"
                    data-testid="hero-subtitle"
                >
                    Kompletné technické zabezpečenie eventov – od návrhu
                    riešenia až po bezproblémovú realizáciu priamo na mieste.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: noMotion ? 0 : 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: noMotion ? 0 : 0.8, delay: noMotion ? 0 : 0.55 }}
                    className="mt-10 flex flex-wrap gap-4"
                >
                    <MagneticButton>
                        <Button
                            onClick={() => scrollTo("#contact")}
                            className="group h-13 px-8 rounded-md bg-nova-cyan text-black hover:bg-nova-cyan hover:saturate-125 font-semibold text-base transition-all hover:shadow-[0_0_30px_rgba(0,153,255,0.4)]"
                            data-testid="hero-cta-quote"
                        >
                            Nezáväzná ponuka
                            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </Button>
                    </MagneticButton>
                    <MagneticButton>
                        <Button
                            onClick={() => scrollTo("#services")}
                            variant="outline"
                            className="h-13 px-8 rounded-md border-white/15 text-white bg-white/4 hover:bg-white/8 hover:border-white/30 font-semibold text-base"
                            data-testid="hero-cta-services"
                        >
                            <Play className="mr-2 w-4 h-4" />
                            Pozrieť služby
                        </Button>
                    </MagneticButton>
                </motion.div>

                {/* Stats */}
                <motion.div
                    initial={{ opacity: 0, y: noMotion ? 0 : 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: noMotion ? 0 : 0.9, delay: noMotion ? 0 : 0.75 }}
                    className="mt-20 grid grid-cols-2 sm:grid-cols-4 gap-8 max-w-3xl border-t border-white/10 pt-10"
                    data-testid="hero-stats"
                >
                    {[
                        { num: 20,  suf: "+", l: "rokov skúseností" },
                        { num: 500, suf: "+", l: "akcií"            },
                        { num: 100, suf: "%", l: "podpora na mieste"},
                        { v: "24/7",           l: "komunikácia"     },
                    ].map((s) => (
                        <div key={s.l}>
                            <div className="font-heading text-3xl sm:text-4xl font-black text-white">
                                {s.v ?? <AnimatedCounter target={s.num} suffix={s.suf} duration={2} />}
                            </div>
                            <div className="mt-1 text-xs font-mono uppercase tracking-wider text-zinc-500">
                                {s.l}
                            </div>
                        </div>
                    ))}
                </motion.div>
            </motion.div>

            {/* Brand logos strip — inside hero, above scroll hint */}
            <HeroBrands />

            {/* Scroll hint — desktop only, shown only when no brands bar */}
            {!isMobile && (
                <div className="absolute bottom-[72px] left-1/2 -translate-x-1/2 z-20 hidden sm:flex flex-col items-center gap-2 text-zinc-500">
                    <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Scroll</span>
                    <motion.div
                        animate={noMotion ? {} : { y: [0, 8, 0] }}
                        transition={{ repeat: Infinity, duration: 1.5 }}
                        className="w-px h-8 bg-gradient-to-b from-nova-cyan to-transparent"
                    />
                </div>
            )}
        </section>
    );
}
